import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import FRUpdateFileUpload from './FRUpdateFileUpload';
import FRUpdateCompanyList from './FRUpdateCompanyList';
import { useToast } from '../../shared/contexts/ToastContext';
import { useApiCall } from '../../shared/hooks/useApiCall';
import { useAuth } from '../../auth/contexts/AuthContext';

interface Company {
  company_id: string;
  name: string;
  company_code: string;
  created_at: string;
  created_by_username: string;
  assigned_user?: number[];
  assigned_usernames?: string | null;
}

interface Document {
  document_id: string;
  file_name: string;
  stage: number;
  username: string;
  created_at: string;
  assigned_user?: number[];
  assigned_username?: string;
  assigned_usernames?: string;
}

// Helper function for stage display
const getStageConfig = (stage: number) => {
  if (stage === 0) {
    return {
      label: 'Etiketlendi',
      className: 'bg-blue-500/20 text-blue-700 dark:text-blue-400 border border-blue-500/30'
    };
  } else if (stage >= 1) {
    return {
      label: `v${stage}`,
      className: 'bg-purple-500/20 text-purple-700 dark:text-purple-400 border border-purple-500/30'
    };
  }
  return {
    label: 'Bilinmeyen',
    className: 'bg-gray-500/20 text-gray-700 dark:text-gray-400 border border-gray-500/30'
  };
};

const FRUpdatePage: React.FC = () => {
  const { company_id, document_id } = useParams<{ company_id: string; document_id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  
  // View state
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [selectedDocument, setSelectedDocument] = useState<Document | null>(null);
  
  // Company list state
  const [companies, setCompanies] = useState<Company[]>([]);
  const [isLoadingCompanies, setIsLoadingCompanies] = useState(true);
  
  // Delete modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [companyToDelete, setCompanyToDelete] = useState<Company | null>(null);
  
  // Add company modal state
  const [showAddModal, setShowAddModal] = useState(false);
  const [newCompanyName, setNewCompanyName] = useState('');
  const [newCompanyCode, setNewCompanyCode] = useState('');
  
  // Document list state
  const [documents, setDocuments] = useState<Document[]>([]);
  const [isLoadingDocuments, setIsLoadingDocuments] = useState(false);
  
  // Delete document modal state
  const [showDeleteDocumentModal, setShowDeleteDocumentModal] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState<Document | null>(null);
  
  // Report voice modal state
  const [showReportVoiceModal, setShowReportVoiceModal] = useState(false);
  const [reportVoice, setReportVoice] = useState<'corporate' | 'we_lang' | 'hybrid'>('corporate');
  const [entityName, setEntityName] = useState<string>('');
  const [entityType, setEntityType] = useState<'bank' | 'company' | 'holding' | 'foundation'>('company');
  const [entityNameError, setEntityNameError] = useState<string>('');
  
  // File history modal state
  const [showFileHistoryModal, setShowFileHistoryModal] = useState(false);
  const [fileHistory, setFileHistory] = useState<any[]>([]);
  const [loadingFileHistory, setLoadingFileHistory] = useState(false);
  const [downloadingFilePath, setDownloadingFilePath] = useState<string | null>(null);
  
  // Tag cleaner modal state
  const [showTagCleanerModal, setShowTagCleanerModal] = useState(false);
  const [cleanerFile, setCleanerFile] = useState<File | null>(null);
  const [isCleaningFile, setIsCleaningFile] = useState(false);
  const [cleanerProgress, setCleanerProgress] = useState(0);
  
  // Edit document name modal state
  const [showEditNameModal, setShowEditNameModal] = useState(false);
  const [showEditNameConfirmModal, setShowEditNameConfirmModal] = useState(false);
  const [documentToEdit, setDocumentToEdit] = useState<Document | null>(null);
  const [newDocumentName, setNewDocumentName] = useState('');
  const [isUpdatingName, setIsUpdatingName] = useState(false);
  
  // Edit company code modal state
  const [showEditCodeModal, setShowEditCodeModal] = useState(false);
  const [showEditCodeConfirmModal, setShowEditCodeConfirmModal] = useState(false);
  const [companyToEditCode, setCompanyToEditCode] = useState<Company | null>(null);
  const [editedCompanyCode, setEditedCompanyCode] = useState('');
  const [isUpdatingCode, setIsUpdatingCode] = useState(false);
  
  // File upload state
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [statusMessage, setStatusMessage] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [downloadBlob, setDownloadBlob] = useState<Blob | null>(null);
  const [fileName, setFileName] = useState<string>('');
  
  // Document mismatch modal states
  const [showDocumentMismatchModal, setShowDocumentMismatchModal] = useState(false);
  const [pendingUploadData, setPendingUploadData] = useState<FormData | null>(null);
  const [pendingUploadConfig, setPendingUploadConfig] = useState<{endpoint: string, successMessage: string} | null>(null);

  const { error: showError, success: showSuccess } = useToast();
  const { apiCall } = useApiCall();
  const { user, isFRUpdateAdmin, canManageFRCompanies, canDeleteFRDocuments } = useAuth();

  // Determine current view from URL
  const getCurrentView = (): 'company-list' | 'options-select' | 'file-upload' | 'document-list' | 'document-edit' => {
    if (!company_id) return 'company-list';
    if (location.pathname.includes('/edit')) return 'document-edit';
    if (location.pathname.endsWith('/document-upload')) return 'file-upload';
    if (location.pathname.endsWith('/documents')) return 'document-list';
    return 'options-select';
  };

  const currentView = getCurrentView();

  // Fetch companies on mount
  useEffect(() => {
    fetchCompanies();
  }, []);

  // Fetch company details when company_id is in URL
  useEffect(() => {
    if (company_id && companies.length > 0) {
      const company = companies.find(c => c.company_id === company_id);
      if (company && company.company_id !== selectedCompany?.company_id) {
        setSelectedCompany(company);
      }
    } else if (!company_id && selectedCompany) {
      // URL'de company_id yoksa seçimi temizle
      setSelectedCompany(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company_id, companies]);

  // Fetch documents when on document-list or document-edit view
  useEffect(() => {
    if ((currentView === 'document-list' || currentView === 'document-edit') && selectedCompany) {
      fetchDocuments();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentView, selectedCompany]);

  // Set selected document when document_id is in URL
  useEffect(() => {
    if (document_id && documents.length > 0) {
      const document = documents.find(d => d.document_id === document_id);
      if (document && document.document_id !== selectedDocument?.document_id) {
        setSelectedDocument(document);
      }
    } else if (!document_id && selectedDocument) {
      // URL'de document_id yoksa seçimi temizle
      setSelectedDocument(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [document_id, documents]);

  // Clean up file upload state when leaving file upload views
  useEffect(() => {
    if (currentView !== 'file-upload' && currentView !== 'document-edit') {
      // Clear all file upload related states when not in upload views
      setSelectedFile(null);
      setUploadProgress(0);
      setStatusMessage('');
      setDownloadBlob(null);
      setFileName('');
      setIsDragging(false);
      setIsUploading(false);
    }
  }, [currentView]);

  const fetchCompanies = async () => {
    setIsLoadingCompanies(true);
    try {
      const response = await fetch('https://n8n.mahir.ai/webhook/fr_update_get_companies', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: user?.id?.toString() || ''
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const text = await response.text();
      
      // Check if response is empty or not valid JSON
      if (!text || text.trim() === '') {
        setCompanies([]);
        return;
      }

      try {
        const data = JSON.parse(text);
        // Ensure data is an array and filter out invalid/empty objects
        if (Array.isArray(data)) {
          let validCompanies = data.filter((company: any) => 
            company && 
            typeof company === 'object' && 
            company.company_id && 
            company.name
          );
          
          // FR Update yetkilendirme: Admin değilse sadece atanan şirketleri göster
          if (!isFRUpdateAdmin()) {
            validCompanies = validCompanies.filter((company: any) => {
              const assignedUsernames = company.assigned_usernames || '';
              const assignedUserIds = company.assigned_user || [];
              const userId = company.user_id?.toString() || '';
              const currentUserId = user?.id || 0;
              const currentUsername = user?.name || '';
              
              // Kullanıcı bu şirkete atanmış mı kontrol et
              return assignedUsernames.includes(currentUsername) || 
                     assignedUserIds.includes(currentUserId) ||
                     userId === currentUserId.toString() ||
                     company.created_by === currentUserId.toString();
            });
          }
          
          setCompanies(validCompanies);
        } else {
          setCompanies([]);
        }
      } catch (parseError) {
        console.error('Error parsing companies data:', parseError);
        setCompanies([]);
      }
    } catch (error) {
      console.error('Error fetching companies:', error);
      showError('Hata', 'Şirketler yüklenirken bir hata oluştu');
      setCompanies([]);
    } finally {
      setIsLoadingCompanies(false);
    }
  };

  const handleSelectCompany = (company: Company) => {
    setSelectedCompany(company);
    navigate(`/fr-update/${company.company_id}/documents`);
  };

  const handleBackToCompanies = () => {
    setSelectedCompany(null);
    // Reset file upload state
    setSelectedFile(null);
    setUploadProgress(0);
    setStatusMessage('');
    setDownloadBlob(null);
    setFileName('');
    navigate('/fr-update');
  };

  const handleBackToOptions = () => {
    // Reset file upload state
    setSelectedFile(null);
    setUploadProgress(0);
    setStatusMessage('');
    setDownloadBlob(null);
    setFileName('');
    setIsDragging(false);
    setIsUploading(false);
    if (selectedCompany) {
      navigate(`/fr-update/${selectedCompany.company_id}/documents`);
    }
  };

  const handleNewDocumentUpload = () => {
    // Reset file upload state before navigating
    setSelectedFile(null);
    setUploadProgress(0);
    setStatusMessage('');
    setDownloadBlob(null);
    setFileName('');
    setIsDragging(false);
    setIsUploading(false);
    if (selectedCompany) {
      navigate(`/fr-update/${selectedCompany.company_id}/document-upload`);
    }
  };

  const handleViewDocuments = () => {
    if (selectedCompany) {
      navigate(`/fr-update/${selectedCompany.company_id}/documents`);
    }
  };

  const handleDocumentClick = (doc: Document) => {
    // Reset file upload state before navigating to edit view
    setSelectedFile(null);
    setUploadProgress(0);
    setStatusMessage('');
    setDownloadBlob(null);
    setFileName('');
    setIsDragging(false);
    setIsUploading(false);
    setSelectedDocument(doc);
    if (selectedCompany) {
      navigate(`/fr-update/${selectedCompany.company_id}/documents/${doc.document_id}/edit`);
    }
  };

  const handleBackToDocuments = () => {
    setSelectedDocument(null);
    // Reset file upload state
    setSelectedFile(null);
    setUploadProgress(0);
    setStatusMessage('');
    setDownloadBlob(null);
    setFileName('');
    setIsDragging(false);
    setIsUploading(false);
    if (selectedCompany) {
      navigate(`/fr-update/${selectedCompany.company_id}/documents`);
    }
  };


  const fetchDocuments = async () => {
    if (!selectedCompany) return;
    
    setIsLoadingDocuments(true);
    try {
      const response = await fetch('https://n8n.mahir.ai/webhook/fr_update_get_documents', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          company_id: selectedCompany.company_id
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const text = await response.text();
      
      if (!text || text.trim() === '') {
        setDocuments([]);
        return;
      }

      try {
        const data = JSON.parse(text);
        if (Array.isArray(data)) {
          let validDocuments = data.filter((doc: any) => 
            doc && 
            typeof doc === 'object' && 
            doc.document_id && 
            doc.file_name
          );
          
          // FR Update yetkilendirme: Admin değilse sadece atanan dokümanları göster
          if (!isFRUpdateAdmin()) {
            validDocuments = validDocuments.filter((doc: any) => {
              const assignedUsernames = doc.assigned_usernames || doc.assigned_username || doc.username || '';
              const assignedUserIds = doc.assigned_user || [];
              const userId = doc.user_id?.toString() || '';
              const currentUserId = user?.id || 0;
              const currentUsername = user?.name || '';
              
              // Kullanıcı bu dokümana atanmış mı kontrol et
              return assignedUsernames.includes(currentUsername) || 
                     assignedUserIds.includes(currentUserId) ||
                     userId === currentUserId.toString() ||
                     doc.created_by === currentUserId.toString();
            });
          }
          
          setDocuments(validDocuments);
        } else {
          setDocuments([]);
        }
      } catch (parseError) {
        console.error('Error parsing documents data:', parseError);
        setDocuments([]);
      }
    } catch (error) {
      console.error('Error fetching documents:', error);
      showError('Hata', 'Dokümanlar yüklenirken bir hata oluştu');
      setDocuments([]);
    } finally {
      setIsLoadingDocuments(false);
    }
  };

  const handleDeleteDocument = (doc: Document) => {
    setDocumentToDelete(doc);
    setShowDeleteDocumentModal(true);
  };

  const confirmDeleteDocument = async () => {
    if (!documentToDelete || !user || !selectedCompany) {
      return;
    }

    try {
      const response = await fetch('https://n8n.mahir.ai/webhook/fr_update_delete_documents', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          deleted_by: user.id,
          deleted_at: Math.floor(Date.now() / 1000), // Unix timestamp (epoch)
          company_id: selectedCompany.company_id,
          document_id: documentToDelete.document_id,
        }),
      });

      const data = await response.json();

      if (data.success) {
        showSuccess('Başarılı', 'Doküman başarıyla silindi');
        // Refresh documents list
        await fetchDocuments();
        // Close modal
        setShowDeleteDocumentModal(false);
        setDocumentToDelete(null);
      } else {
        showError('Hata', data.message || 'Doküman silinirken bir hata oluştu');
      }
    } catch (error) {
      console.error('Error deleting document:', error);
      showError('Hata', 'Doküman silinirken bir hata oluştu');
    }
  };

  const cancelDeleteDocument = () => {
    setShowDeleteDocumentModal(false);
    setDocumentToDelete(null);
  };

  const handleDeleteCompany = (companyId: string) => {
    const company = companies.find(c => c.company_id === companyId);
    if (company) {
      setCompanyToDelete(company);
      setShowDeleteModal(true);
    }
  };

  const confirmDeleteCompany = async () => {
    if (!companyToDelete || !user) {
      return;
    }

    try {
      const response = await fetch('https://n8n.mahir.ai/webhook/fr_update_delete_company', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: user.id,
          company_id: companyToDelete.company_id,
          deleted_at: Math.floor(Date.now() / 1000), // Unix timestamp
        }),
      });

      const data = await response.json();

      if (data.success) {
        showSuccess('Başarılı', 'Şirket başarıyla silindi');
        // Refresh companies list
        await fetchCompanies();
        // Close modal
        setShowDeleteModal(false);
        setCompanyToDelete(null);
      } else {
        showError('Hata', data.message || 'Şirket silinirken bir hata oluştu');
      }
    } catch (error) {
      console.error('Error deleting company:', error);
      showError('Hata', 'Şirket silinirken bir hata oluştu');
    }
  };

  const cancelDeleteCompany = () => {
    setShowDeleteModal(false);
    setCompanyToDelete(null);
  };

  const handleAddCompany = () => {
    setShowAddModal(true);
  };

  const confirmAddCompany = async () => {
    if (!newCompanyName.trim() || !newCompanyCode.trim()) {
      showError('Hata', 'Lütfen tüm alanları doldurun');
      return;
    }

    if (!user) {
      showError('Hata', 'Kullanıcı bilgisi bulunamadı');
      return;
    }

    try {
      const response = await fetch('https://n8n.mahir.ai/webhook/fr_update_add_company', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: newCompanyName.trim(),
          company_code: newCompanyCode.trim(),
          created_by: user.id,
        }),
      });

      const data = await response.json();

      // API returns array with company_id: [{ company_id: "..." }]
      if (Array.isArray(data) && data.length > 0 && data[0].company_id) {
        showSuccess('Başarılı', 'Şirket başarıyla eklendi');
        // Refresh companies list
        await fetchCompanies();
        // Close modal and reset form
        setShowAddModal(false);
        setNewCompanyName('');
        setNewCompanyCode('');
      } else {
        showError('Hata', 'Şirket eklenirken bir hata oluştu');
      }
    } catch (error) {
      console.error('Error adding company:', error);
      showError('Hata', 'Şirket eklenirken bir hata oluştu');
    }
  };

  const cancelAddCompany = () => {
    setShowAddModal(false);
    setNewCompanyName('');
    setNewCompanyCode('');
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setStatusMessage('');
    }
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(false);
    
    const file = event.dataTransfer.files[0];
    if (file) {
      setSelectedFile(file);
      setStatusMessage('');
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(false);
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setUploadProgress(0);
    setStatusMessage('');
    setDownloadBlob(null);
    setFileName('');
    setIsDragging(false);
    setIsUploading(false);
  };

  const handleDownload = () => {
    if (!downloadBlob) return;

    const url = window.URL.createObjectURL(downloadBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${fileName}.docx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    showSuccess('Başarılı', 'Dosya indirildi');
  };

  const handleUpload = () => {
    if (!selectedFile) {
      showError('Hata', 'Lütfen bir dosya seçin');
      return;
    }

    if (!selectedCompany) {
      showError('Hata', 'Şirket bilgisi eksik');
      return;
    }

    // Open modal to select report voice
    setShowReportVoiceModal(true);
  };

  const performUpload = async () => {
    if (!entityName.trim()) {
      setEntityNameError('Kurum adı zorunludur, lütfen doldurun.');
      return;
    }
    setEntityNameError('');
    setShowReportVoiceModal(false);

    setIsUploading(true);
    setUploadProgress(0);
    setStatusMessage('Dosya yükleniyor...');
    setDownloadBlob(null); // Önceki blob'u temizle

    const generatedFileName = Date.now().toString();
    setFileName(generatedFileName);

    const formData = new FormData();
    formData.append('file', selectedFile!);
    formData.append('user_id', user?.id?.toString() || '');
    formData.append('company_id', selectedCompany!.company_id);
    formData.append('file_name', generatedFileName);
    formData.append('report_voice', reportVoice);
    formData.append('entity_name', entityName);
    formData.append('entity_type', entityType);

    try {
      const xhr = new XMLHttpRequest();
      xhr.responseType = 'blob'; // Binary dosya için blob response type
      xhr.timeout = 600000; // 10 dakika timeout

      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable) {
          const progress = Math.round((event.loaded / event.total) * 100);
          setUploadProgress(progress);
        }
      });

      xhr.addEventListener('timeout', () => {
        setIsUploading(false);
        showError('Zaman Aşımı', 'İşlem çok uzun sürdü. Lütfen daha sonra tekrar deneyin.');
        console.error('Request timeout');
      });

      xhr.addEventListener('load', async () => {
        if (xhr.status === 200) {
          try {
            const blob = xhr.response;
            
            // Check if response is JSON (error response with has_segment)
            // When has_segment: true, response will be JSON instead of blob file
            const contentType = xhr.getResponseHeader('Content-Type');
            if (contentType && contentType.includes('application/json')) {
              // Response is JSON (error case)
              const text = await blob.text();
              const jsonResponse = JSON.parse(text);
              
              if (jsonResponse.has_segment === true) {
                setIsUploading(false);
                setUploadProgress(0);
                setStatusMessage('');
                showError('Etiket Tespit Edildi', 'Dokümanda etiketler bulundu. Lütfen temiz doküman yükleyiniz.');
                return;
              }
            }
            
            // Normal case: blob file response
            setIsUploading(false);
            setUploadProgress(100);
            
            // n8n'den dönen dosya adını header'lardan al
            let downloadFileName = generatedFileName; // Fallback olarak gönderdiğimiz isim
            
            // Önce Content-Disposition header'ını kontrol et
            const contentDisposition = xhr.getResponseHeader('Content-Disposition');
            if (contentDisposition) {
              const fileNameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
              if (fileNameMatch && fileNameMatch[1]) {
                let extractedFileName = fileNameMatch[1].replace(/['"]/g, '');
                // .docx uzantısını kaldır (çünkü indirirken tekrar ekleyeceğiz)
                extractedFileName = extractedFileName.replace(/\.docx$/i, '');
                downloadFileName = extractedFileName;
              }
            }
            
            // Alternatif olarak custom file_name header'ını kontrol et
            const customFileName = xhr.getResponseHeader('file_name') || xhr.getResponseHeader('x-file-name');
            if (customFileName) {
              downloadFileName = customFileName.replace(/\.docx$/i, '');
            }
            
            // Dosya adını state'e kaydet
            setFileName(downloadFileName);
            
            // Blob'u state'e kaydet
            setDownloadBlob(blob);
            
            showSuccess('Başarılı', 'Doküman indirilmeye hazır');
            setStatusMessage('Doküman etiketlendi ve indirilmeye hazır.');
          } catch (error) {
            setIsUploading(false);
            showError('Hata', 'Dosya işleme sırasında hata oluştu');
          }
        } else {
          setIsUploading(false);
          showError('Hata', `Dosya yüklenirken hata oluştu: ${xhr.status}`);
        }
      });

      xhr.addEventListener('error', () => {
        setIsUploading(false);
        showError('Hata', 'Dosya yüklenirken bir hata oluştu');
      });

      xhr.open('POST', 'https://n8n.mahir.ai/webhook/fr_update_first_file_upload');
      xhr.send(formData);
    } catch (error) {
      setIsUploading(false);
      showError('Hata', 'Dosya yüklenirken bir hata oluştu');
      console.error('Upload error:', error);
    }
  };

  // Check document ID match before upload
  const checkDocumentMatch = async (formData: FormData): Promise<boolean> => {
    try {
      const response = await fetch('https://n8n.mahir.ai/webhook/fr_update_revision_document_id_control', {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.is_match === true;
    } catch (error) {
      console.error('Document check error:', error);
      showError('Hata', 'Doküman kontrolü sırasında hata oluştu');
      return false;
    }
  };

  const handleEditUpload = async () => {
    if (!selectedFile) {
      showError('Hata', 'Lütfen bir dosya seçin');
      return;
    }

    if (!selectedCompany) {
      showError('Hata', 'Şirket bilgisi eksik');
      return;
    }

    if (!selectedDocument) {
      showError('Hata', 'Doküman bilgisi eksik');
      return;
    }

    // Determine endpoint based on current document stage
    let endpoint = '';
    let successMessage = '';
    
    if (selectedDocument.stage === 0) {
      endpoint = 'https://n8n.mahir.ai/webhook/fr_update_revision';
      successMessage = 'v1 başarıyla oluşturuldu';
    } else if (selectedDocument.stage >= 1) {
      endpoint = 'https://n8n.mahir.ai/webhook/fr_update_revision';
      const nextVersion = selectedDocument.stage + 1;
      successMessage = `v${nextVersion} başarıyla yüklendi`;
    } else {
      showError('Hata', 'Bu aşamada dosya yükleyemezsiniz');
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);
    setStatusMessage('Doküman kontrol ediliyor...');
    setDownloadBlob(null); // Önceki blob'u temizle

    const generatedFileName = Date.now().toString();
    setFileName(generatedFileName);

    const formData = new FormData();
    formData.append('file', selectedFile);
    formData.append('user_id', user?.id?.toString() || '');
    formData.append('company_id', selectedCompany.company_id);
    formData.append('file_name', generatedFileName);
    formData.append('document_id', selectedDocument.document_id);

    // Check document match first
    const isMatch = await checkDocumentMatch(formData);
    
    if (!isMatch) {
      // Document mismatch - show confirmation modal
      setIsUploading(false);
      setPendingUploadData(formData);
      setPendingUploadConfig({ endpoint, successMessage });
      setShowDocumentMismatchModal(true);
      return;
    }

    // If match, proceed with upload
    proceedWithUpload(formData, endpoint, successMessage);
  };

  // Actual upload function
  const proceedWithUpload = (formData: FormData, endpoint: string, successMessage: string) => {
    setIsUploading(true);
    setUploadProgress(0);
    setStatusMessage('Dosya yükleniyor...');

    const generatedFileName = formData.get('file_name') as string;

    try {
      const xhr = new XMLHttpRequest();
      xhr.responseType = 'blob'; // Binary dosya için blob response type
      xhr.timeout = 1800000; // 30 dakika timeout

      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable) {
          const progress = Math.round((event.loaded / event.total) * 100);
          setUploadProgress(progress);
        }
      });

      xhr.addEventListener('timeout', () => {
        setIsUploading(false);
        showError('Zaman Aşımı', 'İşlem çok uzun sürdü. Lütfen daha sonra tekrar deneyin.');
        console.error('Request timeout');
      });

      xhr.addEventListener('load', async () => {
        if (xhr.status === 200) {
          try {
            const blob = xhr.response;
            setIsUploading(false);
            setUploadProgress(100);
            
            // n8n'den dönen dosya adını header'lardan al
            let downloadFileName = generatedFileName; // Fallback olarak gönderdiğimiz isim
            
            // Önce Content-Disposition header'ını kontrol et
            const contentDisposition = xhr.getResponseHeader('Content-Disposition');
            if (contentDisposition) {
              const fileNameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
              if (fileNameMatch && fileNameMatch[1]) {
                let extractedFileName = fileNameMatch[1].replace(/['"]/g, '');
                // .docx uzantısını kaldır (çünkü indirirken tekrar ekleyeceğiz)
                extractedFileName = extractedFileName.replace(/\.docx$/i, '');
                downloadFileName = extractedFileName;
              }
            }
            
            // Alternatif olarak custom file_name header'ını kontrol et
            const customFileName = xhr.getResponseHeader('file_name') || xhr.getResponseHeader('x-file-name');
            if (customFileName) {
              downloadFileName = customFileName.replace(/\.docx$/i, '');
            }
            
            // Dosya adını state'e kaydet
            setFileName(downloadFileName);
            
            // Blob'u state'e kaydet
            setDownloadBlob(blob);
            
            showSuccess('Başarılı', successMessage);
            setStatusMessage('Dosya hazır! İndirmek için butona tıklayın.');
            
            // Refresh document status
            await fetchDocuments();
          } catch (error) {
            setIsUploading(false);
            showError('Hata', 'Dosya işleme sırasında hata oluştu');
          }
        } else {
          setIsUploading(false);
          showError('Hata', `Dosya yüklenirken hata oluştu: ${xhr.status}`);
        }
      });

      xhr.addEventListener('error', () => {
        setIsUploading(false);
        showError('Hata', 'Dosya yüklenirken bir hata oluştu');
      });

      xhr.open('POST', endpoint);
      xhr.send(formData);
    } catch (error) {
      setIsUploading(false);
      showError('Hata', 'Dosya yüklenirken bir hata oluştu');
      console.error('Upload error:', error);
    }
  };

  const handleCleanerFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setCleanerFile(file);
    }
  };

  const handleCleanerDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const file = event.dataTransfer.files[0];
    if (file) {
      setCleanerFile(file);
    }
  };

  const handleCleanFile = async () => {
    if (!cleanerFile) {
      showError('Hata', 'Lütfen bir dosya seçin');
      return;
    }

    setIsCleaningFile(true);
    setCleanerProgress(0);

    const formData = new FormData();
    formData.append('file', cleanerFile);
    formData.append('timestamp', Math.floor(Date.now() / 1000).toString()); // Epoch timestamp

    try {
      const xhr = new XMLHttpRequest();
      xhr.responseType = 'blob';
      xhr.timeout = 300000; // 5 dakika timeout

      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable) {
          const progress = Math.round((event.loaded / event.total) * 100);
          setCleanerProgress(progress);
        }
      });

      xhr.addEventListener('timeout', () => {
        setIsCleaningFile(false);
        showError('Zaman Aşımı', 'İşlem çok uzun sürdü. Lütfen daha sonra tekrar deneyin.');
        console.error('Request timeout');
      });

      xhr.addEventListener('load', () => {
        if (xhr.status === 200) {
          const blob = xhr.response;
          
          // Download file
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `cleaned_${cleanerFile.name}`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);

          showSuccess('Başarılı', 'Dosya başarıyla temizlendi ve indirildi');
          setShowTagCleanerModal(false);
          setCleanerFile(null);
          setCleanerProgress(0);
        } else {
          showError('Hata', `Dosya temizlenirken hata oluştu: ${xhr.status}`);
        }
        setIsCleaningFile(false);
      });

      xhr.addEventListener('error', () => {
        setIsCleaningFile(false);
        showError('Hata', 'Dosya temizlenirken bir hata oluştu');
      });

      xhr.open('POST', 'https://n8n.mahir.ai/webhook/fr_update_clean_segments');
      xhr.send(formData);
    } catch (error) {
      setIsCleaningFile(false);
      showError('Hata', 'Dosya temizlenirken bir hata oluştu');
      console.error('Clean error:', error);
    }
  };

  const handleEditCompanyCode = (company: Company) => {
    setCompanyToEditCode(company);
    setEditedCompanyCode(company.company_code);
    setShowEditCodeModal(true);
  };

  const handleConfirmEditCode = () => {
    if (!editedCompanyCode.trim()) {
      showError('Hata', 'Lütfen geçerli bir şirket kodu girin');
      return;
    }
    if (editedCompanyCode.trim() === companyToEditCode?.company_code) {
      showError('Hata', 'Yeni şirket kodu eskisiyle aynı');
      return;
    }
    setShowEditCodeModal(false);
    setShowEditCodeConfirmModal(true);
  };

  const handleUpdateCompanyCode = async () => {
    if (!companyToEditCode) return;

    setIsUpdatingCode(true);

    try {
      const response = await fetch('https://n8n.mahir.ai/webhook/fr_update_change_company_code', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          company_id: companyToEditCode.company_id,
          company_code: editedCompanyCode.trim()
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (Array.isArray(data) && data.length > 0 && data[0].company_code) {
        showSuccess('Başarılı', 'Şirket kodu başarıyla güncellendi');
        // Refresh companies list
        await fetchCompanies();
        setShowEditCodeConfirmModal(false);
        setCompanyToEditCode(null);
        setEditedCompanyCode('');
      } else {
        showError('Hata', 'Şirket kodu güncellenirken bir hata oluştu');
      }
    } catch (error) {
      console.error('Error updating company code:', error);
      showError('Hata', 'Şirket kodu güncellenirken bir hata oluştu');
    } finally {
      setIsUpdatingCode(false);
    }
  };

  const handleEditDocumentName = (doc: Document) => {
    setDocumentToEdit(doc);
    setNewDocumentName(doc.file_name);
    setShowEditNameModal(true);
  };

  const handleConfirmEditName = () => {
    if (!newDocumentName.trim()) {
      showError('Hata', 'Lütfen geçerli bir dosya adı girin');
      return;
    }
    if (newDocumentName.trim() === documentToEdit?.file_name) {
      showError('Hata', 'Yeni dosya adı eskisiyle aynı');
      return;
    }
    setShowEditNameModal(false);
    setShowEditNameConfirmModal(true);
  };

  const handleUpdateDocumentName = async () => {
    if (!documentToEdit) return;

    setIsUpdatingName(true);

    try {
      const response = await fetch('https://n8n.mahir.ai/webhook/fr_update_change_file_name', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          document_id: documentToEdit.document_id,
          file_name: newDocumentName.trim()
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (Array.isArray(data) && data.length > 0 && data[0].file_name) {
        showSuccess('Başarılı', 'Dosya adı başarıyla güncellendi');
        // Refresh documents list
        await fetchDocuments();
        setShowEditNameConfirmModal(false);
        setDocumentToEdit(null);
        setNewDocumentName('');
      } else {
        showError('Hata', 'Dosya adı güncellenirken bir hata oluştu');
      }
    } catch (error) {
      console.error('Error updating document name:', error);
      showError('Hata', 'Dosya adı güncellenirken bir hata oluştu');
    } finally {
      setIsUpdatingName(false);
    }
  };

  const handleOpenFileHistory = async () => {
    if (!selectedDocument) return;
    
    setShowFileHistoryModal(true);
    setLoadingFileHistory(true);
    
    try {
      const response = await fetch('https://n8n.mahir.ai/webhook/fr_update_get_document_paths', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          document_id: selectedDocument.document_id
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (Array.isArray(data) && data.length > 0 && data[0].file_paths) {
        setFileHistory(data[0].file_paths);
      } else {
        setFileHistory([]);
      }
    } catch (error) {
      console.error('Error fetching file history:', error);
      showError('Hata', 'Dosya geçmişi yüklenirken bir hata oluştu');
      setFileHistory([]);
    } finally {
      setLoadingFileHistory(false);
    }
  };

  const handleDownloadFile = async (filePath: string, fileName: string, fileType: string, version: string) => {
    if (!selectedDocument || !selectedCompany) return;
    
    setDownloadingFilePath(filePath);
    
    try {
      const requestBody = {
        file_path: filePath,
        document_id: selectedDocument.document_id,
        company_id: selectedCompany.company_id,
        type: fileType,
        version: version
      };
      
      console.log('Download request:', requestBody);
      
      const response = await fetch('https://n8n.mahir.ai/webhook/fr_update_download_document', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        // Try to get error details
        let errorMessage = `HTTP error! status: ${response.status}`;
        try {
          const errorText = await response.text();
          console.error('Error response:', errorText);
          errorMessage += ` - ${errorText}`;
        } catch (e) {
          console.error('Could not read error response');
        }
        throw new Error(errorMessage);
      }

      const blob = await response.blob();
      
      // Get filename from Content-Disposition header
      let downloadFileName = fileName; // Fallback
      try {
        const contentDisposition = response.headers.get('Content-Disposition');
        if (contentDisposition) {
          const fileNameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
          if (fileNameMatch && fileNameMatch[1]) {
            downloadFileName = fileNameMatch[1].replace(/['"]/g, '');
          }
        }
      } catch (headerError) {
        console.warn('Could not parse Content-Disposition header, using fallback filename');
      }
      
      // Download the file
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = downloadFileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      showSuccess('Başarılı', 'Dosya başarıyla indirildi');
    } catch (error) {
      console.error('Error downloading file:', error);
      showError('Hata', 'Dosya indirilirken bir hata oluştu');
    } finally {
      setDownloadingFilePath(null);
    }
  };

  return (
    <div className="min-h-full">
      {currentView === 'company-list' && (
        <FRUpdateCompanyList
          companies={companies}
          isLoading={isLoadingCompanies}
          onSelectCompany={handleSelectCompany}
          onDeleteCompany={handleDeleteCompany}
          onAddCompany={handleAddCompany}
          onOpenTagCleaner={() => setShowTagCleanerModal(true)}
          onEditCompanyCode={handleEditCompanyCode}
          canManage={canManageFRCompanies()}
        />
      )}

      {currentView === 'options-select' && selectedCompany && (
        <div className="min-h-full flex flex-col">
          {/* Back Button */}
          <div className="w-full max-w-4xl mx-auto mb-4">
            <button
              onClick={handleBackToCompanies}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 transition-colors group"
            >
              <svg className="w-5 h-5 group-hover:-translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7"/>
              </svg>
              <span className="text-sm font-medium">Şirket Seçimine Dön</span>
            </button>
          </div>

          {/* Selected Company Info */}
          <div className="w-full max-w-4xl mx-auto mb-6 lg:mb-12">
            <div className="bg-white/10 dark:bg-gray-900/10 backdrop-blur-3xl border border-white/30 dark:border-white/10 rounded-xl p-3 lg:p-4 pb-2.5 lg:pb-3 shadow-xl">
              <div className="flex items-center gap-2 lg:gap-3">
                <div className="w-10 h-10 lg:w-12 lg:h-12 bg-blue-500/20 dark:bg-blue-500/30 backdrop-blur-sm rounded-lg lg:rounded-xl flex items-center justify-center border border-blue-500/30 dark:border-blue-400/30">
                  <span className="text-base lg:text-lg font-bold text-blue-600 dark:text-blue-400">
                    {selectedCompany.company_code.substring(0, 2).toUpperCase()}
                  </span>
                </div>
                <div>
                  <p className="text-xs lg:text-sm text-blue-600 dark:text-blue-400 font-medium">Seçili Şirket</p>
                  <p className="text-base lg:text-lg font-bold text-gray-900 dark:text-white">{selectedCompany.name}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Options Cards - Bigger, More Colorful, Higher */}
          <div className="flex-1 flex items-start justify-center pt-4 lg:pt-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 lg:gap-8 w-full max-w-4xl px-2 lg:px-0">
              {/* Yeni Doküman Yükle Card */}
              <button
                onClick={handleNewDocumentUpload}
                className="group relative bg-gradient-to-br from-blue-50/60 via-blue-100/50 to-blue-50/60 dark:from-blue-900/15 dark:via-blue-800/12 dark:to-blue-900/15 backdrop-blur-3xl rounded-2xl lg:rounded-3xl shadow-lg border-2 border-blue-200/60 dark:border-blue-400/20 p-6 lg:p-12 hover:bg-gradient-to-br hover:from-blue-100/80 hover:via-blue-150/70 hover:to-blue-100/80 dark:hover:from-blue-900/25 dark:hover:via-blue-800/20 dark:hover:to-blue-900/25 hover:border-blue-400/70 dark:hover:border-blue-400/45 hover:shadow-2xl hover:shadow-blue-500/15 transition-all duration-300"
              >
                <div className="flex flex-col items-center text-center space-y-3 lg:space-y-4">
                  <div className="w-16 h-16 lg:w-20 lg:h-20 bg-gradient-to-br from-blue-400/80 to-blue-500/80 backdrop-blur-sm rounded-xl lg:rounded-2xl flex items-center justify-center shadow-md shadow-blue-400/25 group-hover:shadow-lg group-hover:shadow-blue-400/35 transition-all duration-300">
                    <svg className="w-8 h-8 lg:w-10 lg:h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"></path>
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-lg lg:text-xl font-bold text-gray-900 dark:text-white mb-1 lg:mb-2">Yeni Doküman Yükle</h3>
                    <p className="text-xs lg:text-sm text-gray-700 dark:text-gray-300">Yeni bir doküman yükleyip işleme başlayın</p>
                  </div>
                </div>
              </button>

              {/* Dokümanları Görüntüle Card */}
              <button
                onClick={handleViewDocuments}
                className="group relative bg-gradient-to-br from-emerald-50/60 via-green-100/50 to-emerald-50/60 dark:from-emerald-900/15 dark:via-green-800/12 dark:to-emerald-900/15 backdrop-blur-3xl rounded-2xl lg:rounded-3xl shadow-lg border-2 border-emerald-200/60 dark:border-emerald-400/20 p-6 lg:p-12 hover:bg-gradient-to-br hover:from-emerald-100/80 hover:via-green-150/70 hover:to-emerald-100/80 dark:hover:from-emerald-900/25 dark:hover:via-green-800/20 dark:hover:to-emerald-900/25 hover:border-emerald-400/70 dark:hover:border-emerald-400/45 hover:shadow-2xl hover:shadow-emerald-500/15 transition-all duration-300"
              >
                <div className="flex flex-col items-center text-center space-y-3 lg:space-y-4">
                  <div className="w-16 h-16 lg:w-20 lg:h-20 bg-gradient-to-br from-emerald-400/80 to-green-500/80 backdrop-blur-sm rounded-xl lg:rounded-2xl flex items-center justify-center shadow-md shadow-emerald-400/25 group-hover:shadow-lg group-hover:shadow-emerald-400/35 transition-all duration-300">
                    <svg className="w-8 h-8 lg:w-10 lg:h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-lg lg:text-xl font-bold text-gray-900 dark:text-white mb-1 lg:mb-2">Dokümanları Görüntüle</h3>
                    <p className="text-xs lg:text-sm text-gray-700 dark:text-gray-300">Yüklenen dokümanları görüntüleyin ve yönetin</p>
                  </div>
                </div>
              </button>
            </div>
          </div>
        </div>
      )}

      {currentView === 'file-upload' && selectedCompany && (
        <div className="min-h-full flex flex-col relative bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-gray-900 dark:to-slate-900 rounded-3xl overflow-hidden">
          {/* Content */}
          <div className="relative z-10 flex-1 flex flex-col overflow-hidden">
            {/* Header */}
            <div className="px-4 sm:px-6 py-4 sm:py-6 border-b border-gray-300 dark:border-gray-700 bg-white/60 dark:bg-gray-900/60 backdrop-blur-lg rounded-t-3xl shadow-sm">
              <div className="max-w-6xl mx-auto flex flex-wrap items-center gap-3">
                {/* Geri butonu */}
                <button
                  onClick={handleBackToOptions}
                  className="flex items-center gap-2 text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white transition-colors flex-shrink-0"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7"/>
                  </svg>
                  <span className="font-medium">Geri Dön</span>
                </button>

                {/* Başlık — orta */}
                <div className="flex-1 min-w-0 text-center">
                  <div className="flex items-center justify-center gap-2 mb-0.5">
                    <div className="w-7 h-7 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
                      <svg className="w-4 h-4 text-emerald-600 dark:text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4"></path>
                      </svg>
                    </div>
                    <h1 className="text-lg sm:text-2xl font-bold text-gray-900 dark:text-white truncate">Yeni Doküman Süreci Başlat</h1>
                  </div>
                  <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 hidden sm:block">Temiz Word dokümanınızı yükleyin, sistem otomatik olarak etiketleyip size verecektir</p>
                </div>

                {/* Şirket rozeti */}
                <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-100 dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg flex-shrink-0">
                  <svg className="w-4 h-4 text-gray-600 dark:text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"></path>
                  </svg>
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300 max-w-[8rem] truncate">{selectedCompany.name}</span>
                </div>
              </div>
            </div>

            {/* Main Content */}
            <div className="flex-1 flex items-start justify-center px-3 sm:px-6 pt-4 sm:pt-8 pb-4 sm:pb-8">
              <div className="w-full max-w-6xl">

                {/* Process Flow - Compact */}
                <div className="mb-5">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3 relative">
                    {/* Arrow 1 */}
                    <div className="hidden md:block absolute top-1/2 left-1/3 w-6 -translate-x-1/2 -translate-y-1/2 z-10">
                      <svg className="w-6 h-6 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7l5 5m0 0l-5 5m5-5H6"></path>
                      </svg>
                    </div>
                    {/* Arrow 2 */}
                    <div className="hidden md:block absolute top-1/2 left-2/3 w-6 -translate-x-1/2 -translate-y-1/2 z-10">
                      <svg className="w-6 h-6 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7l5 5m0 0l-5 5m5-5H6"></path>
                      </svg>
                    </div>

                    {/* Step 1 */}
                    <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-300 dark:border-gray-700 p-4">
                      <div className="absolute -top-2 -right-2 w-8 h-8 bg-emerald-600 rounded-full flex items-center justify-center text-white font-bold text-sm shadow-md">
                        1
                      </div>
                      <div className="flex flex-col items-center text-center">
                        <div className="w-14 h-14 bg-emerald-100 dark:bg-emerald-900/30 rounded-xl flex items-center justify-center mb-3">
                          <svg className="w-7 h-7 text-emerald-600 dark:text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"></path>
                          </svg>
                        </div>
                        <h3 className="text-sm font-bold text-gray-900 dark:text-white mb-1">Temiz Doküman Yüklenir</h3>
                        <p className="text-xs text-gray-600 dark:text-gray-400">Word formatında temiz doküman yüklenir</p>
                      </div>
                    </div>

                    {/* Step 2 */}
                    <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-300 dark:border-gray-700 p-4">
                      <div className="absolute -top-2 -right-2 w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold text-sm shadow-md">
                        2
                      </div>
                      <div className="flex flex-col items-center text-center">
                        <div className="w-14 h-14 bg-blue-100 dark:bg-blue-900/30 rounded-xl flex items-center justify-center mb-3">
                          <svg className="w-7 h-7 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"></path>
                          </svg>
                        </div>
                        <h3 className="text-sm font-bold text-gray-900 dark:text-white mb-1">Sistem Dokümanı Etiketler</h3>
                        <p className="text-xs text-gray-600 dark:text-gray-400">Sistem tarafından doküman otomatik etiketlenir</p>
                      </div>
                    </div>

                    {/* Step 3 */}
                    <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-300 dark:border-gray-700 p-4">
                      <div className="absolute -top-2 -right-2 w-8 h-8 bg-teal-600 rounded-full flex items-center justify-center text-white font-bold text-sm shadow-md">
                        3
                      </div>
                      <div className="flex flex-col items-center text-center">
                        <div className="w-14 h-14 bg-teal-100 dark:bg-teal-900/30 rounded-xl flex items-center justify-center mb-3">
                          <svg className="w-7 h-7 text-teal-600 dark:text-teal-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                          </svg>
                        </div>
                        <h3 className="text-sm font-bold text-gray-900 dark:text-white mb-1">Yeni Doküman Süreci Başlar</h3>
                        <p className="text-xs text-gray-600 dark:text-gray-400">Yeni doküman süreci başlar ve doküman etiketlenmiş halde kullanıcıya sunulur</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Upload Area with Warning */}
                <div className="max-w-3xl mx-auto">
                  {/* Info Message */}
                  <div className="mb-3 flex items-center justify-center gap-2 text-xs text-emerald-700 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-lg px-3 py-2">
                    <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                    </svg>
                    <span><strong>Bilgilendirme:</strong> Bu ekran sadece yeni doküman süreci başlatmak içindir. Revize için doküman listesinden seçim yapın.</span>
                  </div>

              <FRUpdateFileUpload
                selectedFile={selectedFile}
                uploadProgress={uploadProgress}
                statusMessage={statusMessage}
                isDragging={isDragging}
                isUploading={isUploading}
                downloadBlob={downloadBlob}
                onFileChange={handleFileChange}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onRemoveFile={handleRemoveFile}
                onUpload={handleUpload}
                onDownload={handleDownload}
                    title="Temiz Doküman Yükle"
              />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {currentView === 'document-list' && selectedCompany && (
        <div className="min-h-full flex items-center justify-center p-4">
          <div className="w-full max-w-7xl min-h-full flex flex-col">
            {/* Header */}
            <div className="relative z-10 bg-white/80 dark:bg-slate-800/80 backdrop-blur-xl border-2 border-gray-200 dark:border-gray-700 border-b-0 rounded-t-2xl p-5 shadow-lg">
              <div className="flex items-center justify-between gap-4">
                {/* Left: Back Button */}
            <button
                  onClick={handleBackToCompanies}
                  className="flex items-center gap-2 px-3 py-2 bg-gray-100 dark:bg-slate-700 hover:bg-gray-200 dark:hover:bg-slate-600 text-gray-700 dark:text-gray-300 rounded-lg transition-all group flex-shrink-0"
            >
                  <svg className="w-5 h-5 group-hover:-translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7"/>
              </svg>
                  <span className="text-sm font-medium">Şirket Seçimine Dön</span>
            </button>
                
                {/* Center: Company Name as Title with Icon */}
                <div className="flex-1 flex items-center justify-center gap-3">
                  <div className="w-10 h-10 bg-blue-600 dark:bg-blue-500 rounded-lg flex items-center justify-center">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"></path>
                    </svg>
                </div>
                  <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{selectedCompany.name}</h1>
                </div>

                {/* Right: New Document Button - Only show when there are documents */}
                {documents.length > 0 ? (
                  <button
                    onClick={handleNewDocumentUpload}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white rounded-lg font-medium transition-all flex-shrink-0"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4"></path>
                    </svg>
                    <span className="text-sm">Yeni Doküman Süreci Başlat</span>
                  </button>
                ) : (
                  <div className="flex-shrink-0" style={{ width: '220px' }}></div>
                )}
            </div>
          </div>

            {/* Content Container */}
            <div className="relative z-0 flex-1 bg-gray-50 dark:bg-slate-900 border-2 border-t-0 border-gray-200 dark:border-gray-700 rounded-b-2xl shadow-2xl p-6 overflow-hidden flex flex-col">
          <div className="flex-1 overflow-y-auto thin-scrollbar">
            {isLoadingDocuments ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <div className="w-16 h-16 border-4 border-blue-500/30 border-t-blue-500 rounded-full animate-spin mx-auto mb-4"></div>
                  <p className="text-gray-600 dark:text-gray-400">Dokümanlar yükleniyor...</p>
                </div>
              </div>
            ) : documents.length === 0 ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <div className="w-24 h-24 bg-blue-100 dark:bg-blue-900/30 rounded-2xl flex items-center justify-center mx-auto mb-6">
                    <svg className="w-12 h-12 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                    </svg>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">Henüz Doküman Yok</h3>
                  <p className="text-gray-600 dark:text-gray-400 mb-6">Bu şirket için henüz yüklenmiş doküman bulunmuyor</p>
                  <button
                    onClick={handleNewDocumentUpload}
                    className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white rounded-lg font-medium transition-all"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4"></path>
                    </svg>
                    <span>Yeni Doküman Süreci Başlat</span>
                  </button>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 pt-2">
                {documents.map((doc) => (
                  <div
                    key={doc.document_id}
                    onClick={() => handleDocumentClick(doc)}
                    className="relative z-0 hover:z-20 bg-white dark:bg-slate-800 border border-gray-200 dark:border-gray-700 rounded-xl p-5 shadow-md hover:shadow-xl hover:border-blue-400 dark:hover:border-blue-500 hover:-translate-y-1 transition-all duration-200 flex flex-col group cursor-pointer"
                  >
                    {/* Action Buttons */}
                    <div className="absolute top-3 right-3 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                      {/* Edit Name Button */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditDocumentName(doc);
                        }}
                        className="w-8 h-8 bg-blue-500/20 hover:bg-blue-500/30 border border-blue-500/30 hover:border-blue-500/50 rounded-lg flex items-center justify-center transition-all duration-200"
                        title="Dosya Adını Düzenle"
                      >
                        <svg className="w-4 h-4 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path>
                        </svg>
                      </button>
                      
                      {/* Delete Button - Sadece Admin görebilir */}
                      {canDeleteFRDocuments() && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteDocument(doc);
                          }}
                          className="w-8 h-8 bg-red-500/20 hover:bg-red-500/30 border border-red-500/30 hover:border-red-500/50 rounded-lg flex items-center justify-center transition-all duration-200"
                          title="Dokümanı Sil"
                        >
                          <svg className="w-4 h-4 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                          </svg>
                        </button>
                      )}
                    </div>

                    {/* File Icon */}
                    <div className="w-16 h-16 bg-gradient-to-br from-blue-400/80 to-blue-500/80 backdrop-blur-sm rounded-xl flex items-center justify-center shadow-md mx-auto mb-4">
                      <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                      </svg>
                    </div>

                    {/* File Name */}
                    <h4 className="text-sm font-bold text-gray-900 dark:text-white text-center mb-3 line-clamp-2 min-h-[2.5rem]">
                      {doc.file_name}
                    </h4>

                    {/* Stage Badge */}
                    <div className="mb-3 flex justify-center">
                      {(() => {
                        const stageConfig = getStageConfig(doc.stage);
                        return (
                          <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-semibold ${stageConfig.className}`}>
                            {stageConfig.label}
                            {doc.stage >= 1 && <span className="opacity-70"> Oluşturuldu</span>}
                          </span>
                        );
                      })()}
                    </div>

                    {/* Divider */}
                    <div className="h-px bg-gradient-to-r from-transparent via-white/30 dark:via-white/20 to-transparent mb-3"></div>

                    {/* Assigned Users */}
                    {(() => {
                      const assignedUsers = doc.assigned_usernames || doc.assigned_username || doc.username;
                      const userList = assignedUsers ? assignedUsers.split(',').map(u => u.trim()).filter(u => u) : [];
                      
                      if (userList.length === 0) return null;
                      
                      return (
                        <div className="mb-2">
                          <div className="flex items-center justify-center gap-1.5 mb-1.5">
                            <svg className="w-4 h-4 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"></path>
                            </svg>
                            <span className="text-xs font-medium text-gray-600 dark:text-gray-400">
                              {userList.length === 1 ? 'Atanan' : 'Atananlar'}:
                            </span>
                          </div>
                          <div className="flex flex-wrap gap-1.5 justify-center">
                            {userList.slice(0, 3).map((username, idx) => (
                              <span 
                                key={idx}
                                className="inline-flex items-center px-2 py-0.5 bg-blue-500/20 dark:bg-blue-500/30 text-blue-700 dark:text-blue-300 rounded text-xs font-medium border border-blue-500/30"
                              >
                                {username}
                              </span>
                            ))}
                            {userList.length > 3 && (
                              <span className="inline-flex items-center px-2 py-0.5 bg-gray-500/20 dark:bg-gray-500/30 text-gray-700 dark:text-gray-300 rounded text-xs font-medium border border-gray-500/30">
                                +{userList.length - 3}
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })()}

                    {/* Date Info */}
                    <div className="flex items-center justify-center gap-1.5">
                      <svg className="w-4 h-4 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                      </svg>
                      <span className="text-xs text-gray-700 dark:text-gray-300 text-center">
                        {new Date(doc.created_at).toLocaleDateString('tr-TR', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric'
                        })}
                      </span>
                    </div>
                    <div className="text-center text-xs text-gray-600 dark:text-gray-400 mt-1">
                      {new Date(doc.created_at).toLocaleTimeString('tr-TR', {
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
            </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && companyToDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="rounded-2xl shadow-2xl max-w-md w-full p-6 border border-gray-200 dark:border-gray-700" style={{ backgroundColor: '#111827' }}>
            {/* Header */}
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-red-500/20 dark:bg-red-500/30 rounded-xl flex items-center justify-center">
                <svg className="w-6 h-6 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path>
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900 dark:text-white">Şirketi Sil</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">Bu işlem geri alınamaz</p>
              </div>
            </div>

            {/* Content */}
            <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-900/50 rounded-xl border border-gray-200 dark:border-gray-700">
              <p className="text-sm text-gray-700 dark:text-gray-300 mb-3">
                Aşağıdaki şirketi silmek istediğinizden emin misiniz?
              </p>
              <div className="flex items-center gap-3 p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                <div className="w-10 h-10 bg-blue-500/20 dark:bg-blue-500/30 rounded-lg flex items-center justify-center border border-blue-500/30">
                  <span className="text-sm font-bold text-blue-600 dark:text-blue-400">
                    {companyToDelete.company_code.substring(0, 2).toUpperCase()}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-gray-900 dark:text-white truncate">
                    {companyToDelete.name}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {companyToDelete.company_code}
                  </p>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              <button
                onClick={cancelDeleteCompany}
                className="flex-1 px-4 py-2.5 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg font-medium transition-colors"
              >
                İptal
              </button>
              <button
                onClick={confirmDeleteCompany}
                className="flex-1 px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors shadow-md hover:shadow-lg"
              >
                Sil
              </button>
            </div>
          </div>
        </div>
      )}

      {currentView === 'document-edit' && selectedCompany && selectedDocument && (
        <div className="min-h-full flex flex-col relative bg-gradient-to-br from-gray-50 to-slate-100 dark:from-gray-900 dark:to-slate-900 rounded-3xl overflow-hidden">
          {/* Content */}
          <div className="relative z-10 flex-1 flex flex-col overflow-hidden">
            {/* Header */}
            <div className="px-3 sm:px-4 py-3 sm:py-4 border-b border-gray-300 dark:border-gray-700 bg-white/60 dark:bg-gray-900/60 backdrop-blur-lg rounded-t-3xl shadow-sm">
              <div className="max-w-full mx-auto">
                {/* Satır 1: Geri + Başlık + Geçmiş */}
                <div className="flex items-center gap-2 mb-2">
                  {/* Geri butonu */}
                  <button
                    onClick={handleBackToDocuments}
                    className="flex items-center gap-1.5 text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white transition-colors flex-shrink-0"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7"/>
                    </svg>
                    <span className="font-medium text-sm sm:text-base hidden sm:inline">Geri Dön</span>
                  </button>

                  {/* Başlık — ortada, flex-1 */}
                  <div className="flex-1 flex items-center justify-center gap-2 min-w-0">
                    <div className="w-8 h-8 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
                      <svg className="w-4 h-4 sm:w-5 sm:h-5 text-indigo-600 dark:text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
                      </svg>
                    </div>
                    <h1 className="text-lg sm:text-2xl lg:text-3xl font-bold text-gray-900 dark:text-white truncate">Doküman Revizyonu</h1>
                  </div>

                  {/* Geçmiş butonu */}
                  <button
                    onClick={handleOpenFileHistory}
                    className="flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-4 py-1.5 sm:py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-medium transition-all shadow-sm flex-shrink-0 text-xs sm:text-sm"
                    title="Dosya Geçmişi"
                  >
                    <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                    </svg>
                    <span className="hidden sm:inline">Dosya Geçmişi</span>
                  </button>
                </div>

                {/* Second Row: Company + Document Name */}
                <div className="flex items-center justify-center gap-2 text-sm lg:text-base xl:text-lg">
                  <div className="flex items-center gap-1.5 lg:gap-2 text-gray-600 dark:text-gray-400">
                    <svg className="w-4 h-4 lg:w-5 lg:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"></path>
                    </svg>
                    <span className="font-medium">{selectedCompany.name}</span>
                  </div>
                  <span className="text-gray-400 dark:text-gray-600">•</span>
                  <span className="text-gray-600 dark:text-gray-400">{selectedDocument.file_name}</span>
                </div>
              </div>
            </div>

            {/* Main Content */}
            <div className="flex-1 flex items-start justify-center pt-1 pb-5 overflow-y-auto">
              <div className="w-full min-h-full">
                
                {/* Unified Container: Info + Progress + Upload */}
                <div className="bg-white dark:bg-gray-800 shadow-md border border-gray-300 dark:border-gray-700 overflow-hidden min-h-full rounded-b-3xl">
                  
                  {/* Info Message - Top Section */}
                  <div className="bg-indigo-50 dark:bg-indigo-900/20 border-b border-indigo-200 dark:border-indigo-800 px-6 lg:px-8 py-2.5 lg:py-3.5">
                    <div className="flex items-center justify-center gap-2 text-sm lg:text-base text-indigo-700 dark:text-indigo-400">
                      <svg className="w-5 h-5 lg:w-6 lg:h-6 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                      </svg>
                      <span><strong>Bilgilendirme:</strong> Bu ekran mevcut dokümanı revize etmek içindir. Revize edilmiş Word dokümanınızı yükleyin.</span>
                    </div>
                  </div>

                  {/* Progress Timeline Section */}
                  <div className="px-6 py-4 lg:px-8 lg:py-5 border-b border-gray-200 dark:border-gray-700">
                    <h3 className="text-sm lg:text-base font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                      <svg className="w-5 h-5 lg:w-6 lg:h-6 text-indigo-600 dark:text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"></path>
                      </svg>
                      İlerleme Durumu
                    </h3>
                    
                    <div className="relative flex items-center justify-center min-h-[120px]">
                      {/* Aktif Adım - Her Zaman Merkezde */}
                      <div className="absolute left-1/2 transform -translate-x-1/2 flex flex-col items-center z-10">
                        <div className="w-12 h-12 lg:w-14 lg:h-14 rounded-full flex items-center justify-center mb-2 bg-blue-500 border-4 border-blue-200 dark:border-blue-900 shadow-xl animate-pulse">
                          <svg className="w-6 h-6 lg:w-7 lg:h-7 text-white animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
                          </svg>
                        </div>
                        <p className="text-base lg:text-lg font-bold text-center text-blue-600 dark:text-blue-400 mb-1 whitespace-nowrap">
                          v{selectedDocument.stage + 1}
                        </p>
                        <p className="text-xs lg:text-sm text-center text-blue-600 dark:text-blue-400 font-semibold whitespace-nowrap">
                          Şimdi oluşturun
                        </p>
                      </div>

                      {/* Tamamlanan Adımlar - Sola Doğru Genişler */}
                      <div className="absolute left-0 right-1/2 flex items-center justify-end gap-3 pr-12 overflow-hidden">
                        {/* Etiketlendi - Her zaman tamamlanmış */}
                        <div className="flex flex-col items-center">
                          <div className="w-10 h-10 lg:w-11 lg:h-11 rounded-full flex items-center justify-center mb-2 bg-green-500 border-4 border-green-200 dark:border-green-900 shadow-lg">
                            <svg className="w-5 h-5 lg:w-6 lg:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7"></path>
                            </svg>
                          </div>
                          <p className="text-sm lg:text-base font-bold text-center text-green-600 dark:text-green-400 whitespace-nowrap">
                            Etiketlendi
                          </p>
                        </div>

                        {/* v1 - Eğer stage >= 1 ise tamamlanmış */}
                        {selectedDocument.stage >= 1 && (
                          <>
                            <svg className="w-6 h-6 text-green-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7l5 5m0 0l-5 5m5-5H6"></path>
                            </svg>
                            <div className="flex flex-col items-center">
                              <div className="w-10 h-10 lg:w-11 lg:h-11 rounded-full flex items-center justify-center mb-2 bg-green-500 border-4 border-green-200 dark:border-green-900 shadow-lg">
                                <svg className="w-5 h-5 lg:w-6 lg:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7"></path>
                                </svg>
                              </div>
                              <p className="text-sm lg:text-base font-bold text-center text-green-600 dark:text-green-400 whitespace-nowrap">
                                v1
                              </p>
                            </div>
                          </>
                        )}

                        {/* v2 - Eğer stage >= 2 ise tamamlanmış */}
                        {selectedDocument.stage >= 2 && (
                          <>
                            <svg className="w-6 h-6 text-green-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7l5 5m0 0l-5 5m5-5H6"></path>
                            </svg>
                            <div className="flex flex-col items-center">
                              <div className="w-10 h-10 lg:w-11 lg:h-11 rounded-full flex items-center justify-center mb-2 bg-green-500 border-4 border-green-200 dark:border-green-900 shadow-lg">
                                <svg className="w-5 h-5 lg:w-6 lg:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7"></path>
                                </svg>
                              </div>
                              <p className="text-sm lg:text-base font-bold text-center text-green-600 dark:text-green-400 whitespace-nowrap">
                                v2
                              </p>
                            </div>
                          </>
                        )}

                        {/* v3+ - Eğer stage > 2 ise göster */}
                        {selectedDocument.stage > 2 && (
                          <>
                            {Array.from({ length: selectedDocument.stage - 2 }, (_, i) => (
                              <React.Fragment key={i}>
                                <svg className="w-6 h-6 text-green-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7l5 5m0 0l-5 5m5-5H6"></path>
                                </svg>
                                <div className="flex flex-col items-center">
                                  <div className="w-10 h-10 lg:w-11 lg:h-11 rounded-full flex items-center justify-center mb-2 bg-green-500 border-4 border-green-200 dark:border-green-900 shadow-lg">
                                    <svg className="w-5 h-5 lg:w-6 lg:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7"></path>
                                    </svg>
                                  </div>
                                  <p className="text-sm lg:text-base font-bold text-center text-green-600 dark:text-green-400 whitespace-nowrap">
                                    v{i + 3}
                                  </p>
                                </div>
                              </React.Fragment>
                            ))}
                          </>
                        )}

                        {/* Ok işareti - Aktif adıma doğru */}
                        <svg className="w-8 h-8 text-blue-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7l5 5m0 0l-5 5m5-5H6"></path>
                        </svg>
                      </div>
                    </div>
                  </div>

                  {/* File Upload Section */}
                  {(selectedDocument.stage === 0 || selectedDocument.stage >= 1) && (
                    <div className="px-6 py-5 lg:px-8 lg:py-6">
                      <FRUpdateFileUpload
                selectedFile={selectedFile}
                uploadProgress={uploadProgress}
                statusMessage={statusMessage}
                isDragging={isDragging}
                isUploading={isUploading}
                downloadBlob={downloadBlob}
                onFileChange={handleFileChange}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onRemoveFile={handleRemoveFile}
                onUpload={handleEditUpload}
                onDownload={handleDownload}
                title={selectedDocument.stage === 0 ? 'v1 Oluştur' : `v${selectedDocument.stage + 1} Oluştur`}
                isRevision={true}
              />
                    </div>
            )}
                  
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Document Mismatch Confirmation Modal */}
      {showDocumentMismatchModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="rounded-2xl shadow-2xl max-w-md w-full p-6 border border-gray-200 dark:border-gray-700" style={{ backgroundColor: '#111827' }}>
            {/* Header */}
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-orange-500/20 dark:bg-orange-500/30 rounded-xl flex items-center justify-center">
                <svg className="w-6 h-6 text-orange-600 dark:text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path>
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-bold text-white">Farklı Doküman Tespit Edildi</h3>
                <p className="text-sm text-gray-400">Onay gerekiyor</p>
              </div>
            </div>

            {/* Content */}
            <div className="mb-6 p-4 bg-gray-900/50 rounded-xl border border-gray-700">
              <p className="text-sm text-gray-300 mb-3">
                Yüklenen doküman, sistemdeki mevcut doküman ile eşleşmiyor.
              </p>
              <p className="text-sm text-orange-400 font-medium">
                Yüklenen dokümanı onaylıyor musunuz?
              </p>
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowDocumentMismatchModal(false);
                  setPendingUploadData(null);
                  setPendingUploadConfig(null);
                  setStatusMessage('');
                }}
                className="flex-1 px-4 py-2.5 bg-gray-700 hover:bg-gray-600 text-gray-200 rounded-lg font-medium transition-colors"
              >
                Hayır, İptal Et
              </button>
              <button
                onClick={() => {
                  setShowDocumentMismatchModal(false);
                  if (pendingUploadData && pendingUploadConfig) {
                    proceedWithUpload(pendingUploadData, pendingUploadConfig.endpoint, pendingUploadConfig.successMessage);
                  }
                  setPendingUploadData(null);
                  setPendingUploadConfig(null);
                }}
                className="flex-1 px-4 py-2.5 bg-orange-600 hover:bg-orange-700 text-white rounded-lg font-medium transition-colors shadow-md hover:shadow-lg"
              >
                Evet, Onayla
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Document Confirmation Modal */}
      {showDeleteDocumentModal && documentToDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="rounded-2xl shadow-2xl max-w-md w-full p-6 border border-gray-200 dark:border-gray-700" style={{ backgroundColor: '#111827' }}>
            {/* Header */}
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-red-500/20 dark:bg-red-500/30 rounded-xl flex items-center justify-center">
                <svg className="w-6 h-6 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path>
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-bold text-white">Dokümanı Sil</h3>
                <p className="text-sm text-gray-400">Bu işlem geri alınamaz</p>
              </div>
            </div>

            {/* Content */}
            <div className="mb-6 p-4 bg-gray-900/50 rounded-xl border border-gray-700">
              <p className="text-sm text-gray-300 mb-3">
                Aşağıdaki dokümanı silmek istediğinizden emin misiniz?
              </p>
              <div className="flex items-start gap-3 p-3 bg-gray-800 rounded-lg border border-gray-700">
                <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center border border-blue-500/30 flex-shrink-0">
                  <svg className="w-5 h-5 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                  </svg>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-white text-sm break-words">
                    {documentToDelete.file_name}
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    {documentToDelete.username} • {new Date(documentToDelete.created_at).toLocaleDateString('tr-TR')}
                  </p>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              <button
                onClick={cancelDeleteDocument}
                className="flex-1 px-4 py-2.5 bg-gray-700 hover:bg-gray-600 text-gray-200 rounded-lg font-medium transition-colors"
              >
                İptal
              </button>
              <button
                onClick={confirmDeleteDocument}
                className="flex-1 px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors shadow-md hover:shadow-lg"
              >
                Sil
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Company Modal */}
      {showDeleteModal && companyToDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="rounded-2xl shadow-2xl max-w-md w-full p-6 border border-gray-200 dark:border-gray-700" style={{ backgroundColor: '#111827' }}>
            {/* Header */}
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-red-500/20 dark:bg-red-500/30 rounded-xl flex items-center justify-center">
                <svg className="w-6 h-6 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path>
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-bold text-white">Şirketi Sil</h3>
                <p className="text-sm text-gray-400">Bu işlem geri alınamaz</p>
              </div>
            </div>

            {/* Content */}
            <div className="mb-6 p-4 bg-gray-900/50 rounded-xl border border-gray-700">
              <p className="text-sm text-gray-300 mb-3">
                Aşağıdaki şirketi silmek istediğinizden emin misiniz?
              </p>
              <div className="flex items-center gap-3 p-3 bg-gray-800 rounded-lg border border-gray-700">
                <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center border border-blue-500/30">
                  <span className="text-sm font-bold text-blue-400">
                    {companyToDelete.company_code.substring(0, 2).toUpperCase()}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-white truncate">
                    {companyToDelete.name}
                  </p>
                  <p className="text-xs text-gray-400">
                    {companyToDelete.company_code}
                  </p>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              <button
                onClick={cancelDeleteCompany}
                className="flex-1 px-4 py-2.5 bg-gray-700 hover:bg-gray-600 text-gray-200 rounded-lg font-medium transition-colors"
              >
                İptal
              </button>
              <button
                onClick={confirmDeleteCompany}
                className="flex-1 px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors shadow-md hover:shadow-lg"
              >
                Sil
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Company Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="rounded-2xl shadow-2xl max-w-md w-full p-6 border border-gray-200 dark:border-gray-700" style={{ backgroundColor: '#111827' }}>
            {/* Header */}
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-blue-500/20 dark:bg-blue-500/30 rounded-xl flex items-center justify-center">
                <svg className="w-6 h-6 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-bold text-white">Yeni Şirket Ekle</h3>
                <p className="text-sm text-gray-400">Şirket bilgilerini girin</p>
              </div>
            </div>

            {/* Form */}
            <div className="space-y-4 mb-6">
              {/* Company Name */}
              <div>
                <label htmlFor="companyName" className="block text-sm font-medium text-gray-300 mb-2">
                  Şirket İsmi
                </label>
                <input
                  type="text"
                  id="companyName"
                  value={newCompanyName}
                  onChange={(e) => setNewCompanyName(e.target.value)}
                  placeholder="Örn: ABC Teknoloji A.Ş."
                  className="w-full px-4 py-2.5 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                />
              </div>

              {/* Company Code */}
              <div>
                <label htmlFor="companyCode" className="block text-sm font-medium text-gray-300 mb-2">
                  Şirket Kodu
                </label>
                <input
                  type="text"
                  id="companyCode"
                  value={newCompanyCode}
                  onChange={(e) => setNewCompanyCode(e.target.value)}
                  placeholder="Örn: ABC123"
                  className="w-full px-4 py-2.5 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                />
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              <button
                onClick={cancelAddCompany}
                className="flex-1 px-4 py-2.5 bg-gray-700 hover:bg-gray-600 text-gray-200 rounded-lg font-medium transition-colors"
              >
                İptal
              </button>
              <button
                onClick={confirmAddCompany}
                disabled={!newCompanyName.trim() || !newCompanyCode.trim()}
                className={`flex-1 px-4 py-2.5 rounded-lg font-medium transition-colors shadow-md ${
                  !newCompanyName.trim() || !newCompanyCode.trim()
                    ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                    : 'bg-blue-600 hover:bg-blue-700 text-white hover:shadow-lg'
                }`}
              >
                Ekle
              </button>
            </div>
          </div>
        </div>
      )}

      {/* File History Timeline Modal */}
      {showFileHistoryModal && selectedDocument && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="rounded-2xl shadow-2xl max-w-3xl w-full max-h-[85vh] overflow-hidden border border-gray-200 dark:border-gray-700" style={{ backgroundColor: '#111827' }}>
            {/* Header */}
            <div className="flex items-center justify-between gap-3 p-6 border-b border-gray-700">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-purple-500/20 dark:bg-purple-500/30 rounded-xl flex items-center justify-center">
                  <svg className="w-6 h-6 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white">Dosya İşlem Geçmişi</h3>
                  <p className="text-sm text-gray-400">{selectedDocument.file_name}</p>
                </div>
              </div>
              <button
                onClick={() => setShowFileHistoryModal(false)}
                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-700 transition-colors"
              >
                <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                </svg>
              </button>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto max-h-[calc(85vh-120px)] thin-scrollbar">
              {loadingFileHistory ? (
                <div className="flex items-center justify-center py-12">
                  <div className="text-center">
                    <div className="w-12 h-12 border-4 border-purple-500/30 border-t-purple-500 rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="text-gray-400">Dosyalar yükleniyor...</p>
                  </div>
                </div>
              ) : fileHistory.length === 0 ? (
                <div className="flex items-center justify-center py-12">
                  <div className="text-center">
                    <div className="w-16 h-16 bg-gray-700 rounded-2xl flex items-center justify-center mx-auto mb-4">
                      <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                      </svg>
                    </div>
                    <h4 className="text-white font-semibold mb-2">Dosya Bulunamadı</h4>
                    <p className="text-gray-400 text-sm">Bu doküman için henüz işlenmiş dosya bulunmuyor</p>
                  </div>
                </div>
              ) : (
                <div className="relative">
                  {/* Timeline Vertical Line */}
                  <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-gradient-to-b from-blue-500 via-purple-500 to-pink-500"></div>

                  {/* Timeline Items */}
                  <div className="space-y-8">
                    {fileHistory.map((item, index) => {
                      // Determine if this is the first item (original files)
                      const isOriginal = item.original_file_path && item.tagged_file_path;
                      const isRevision = item.revision_input && item.revision_output;
                      
                      let title = '';
                      let files: Array<{path: string, label: string, icon: string, type: string, version: string}> = [];
                      
                      if (isOriginal) {
                        title = 'Orijinal Dosyalar';
                        files = [
                          { path: item.original_file_path, label: 'Orijinal Dosya', icon: 'document', type: 'original', version: 'v0' },
                          { path: item.tagged_file_path, label: 'Etiketlenmiş Dosya', icon: 'tag', type: 'tagged', version: 'v0' }
                        ];
                      } else if (isRevision) {
                        // Version numbering: index 1 = v1, index 2 = v2, etc.
                        const versionStr = `v${index}`;
                        title = versionStr;
                        files = [
                          { path: item.revision_input, label: 'Girdi Dosyası', icon: 'upload', type: 'input', version: versionStr },
                          { path: item.revision_output, label: 'Çıktı Dosyası', icon: 'download', type: 'output', version: versionStr }
                        ];
                      }

                      return (
                        <div key={index} className="relative pl-16">
                          {/* Timeline Node */}
                          <div className={`absolute left-3 top-2 w-6 h-6 rounded-full border-4 border-gray-900 flex items-center justify-center ${
                            isOriginal ? 'bg-blue-500' : 'bg-purple-500'
                          }`}>
                            <div className="w-2 h-2 bg-white rounded-full"></div>
                          </div>

                          {/* Content Card */}
                          <div className="bg-gray-800/50 border border-gray-700 rounded-xl p-4">
                            {/* Title */}
                            <h4 className={`font-bold text-lg mb-4 ${
                              isOriginal ? 'text-blue-400' : 'text-purple-400'
                            }`}>
                              {title}
                            </h4>

                            {/* Files */}
                            <div className="space-y-3">
                              {files.map((file, fileIndex) => {
                                const fileName = file.path.split('/').pop() || 'document.docx';
                                const isDownloading = downloadingFilePath === file.path;
                                
                                return (
                                  <div
                                    key={fileIndex}
                                    className="flex items-center justify-between gap-3 p-3 bg-gray-900/50 rounded-lg border border-gray-700 hover:border-gray-600 transition-colors"
                                  >
                                    <div className="flex items-center gap-3 min-w-0">
                                      {/* File Icon */}
                                      <div className="flex-shrink-0">
                                        {file.icon === 'document' && (
                                          <svg className="w-5 h-5 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                                          </svg>
                                        )}
                                        {file.icon === 'tag' && (
                                          <svg className="w-5 h-5 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"></path>
                                          </svg>
                                        )}
                                        {file.icon === 'upload' && (
                                          <svg className="w-5 h-5 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"></path>
                                          </svg>
                                        )}
                                        {file.icon === 'download' && (
                                          <svg className="w-5 h-5 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10"></path>
                                          </svg>
                                        )}
                                      </div>
                                      {/* File Info */}
                                      <div className="min-w-0">
                                        <p className="text-sm font-medium text-white">{file.label}</p>
                                      </div>
                                    </div>
                                    {/* Download Button */}
                                    <button
                                      onClick={() => handleDownloadFile(file.path, fileName, file.type, file.version)}
                                      disabled={isDownloading}
                                      className={`flex-shrink-0 px-3 py-2 rounded-lg font-medium text-sm transition-colors ${
                                        isDownloading
                                          ? 'bg-gray-700 text-gray-400 cursor-not-allowed'
                                          : 'bg-blue-600 hover:bg-blue-700 text-white'
                                      }`}
                                    >
                                      {isDownloading ? (
                                        <svg className="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
                                        </svg>
                                      ) : (
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"></path>
                                        </svg>
                                      )}
                                    </button>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Tag Cleaner Modal */}
      {showTagCleanerModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="rounded-2xl shadow-2xl max-w-2xl w-full p-6 border border-gray-200 dark:border-gray-700" style={{ backgroundColor: '#111827' }}>
            {/* Header */}
            <div className="flex items-center justify-between gap-3 mb-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-orange-500/20 dark:bg-orange-500/30 rounded-xl flex items-center justify-center">
                  <svg className="w-6 h-6 text-orange-600 dark:text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"></path>
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white">Etiket Temizleyici</h3>
                  <p className="text-sm text-gray-400">Dosyayı etiketlerinden arındır</p>
                </div>
              </div>
              <button
                onClick={() => {
                  setShowTagCleanerModal(false);
                  setCleanerFile(null);
                  setCleanerProgress(0);
                }}
                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-700 transition-colors"
              >
                <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                </svg>
              </button>
            </div>

            {/* Tool Açıklaması */}
            <div className="mb-6 p-4 bg-orange-500/10 border border-orange-500/30 rounded-xl">
              <div className="flex items-start gap-3">
                <svg className="w-5 h-5 text-orange-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
                <div className="flex-1">
                  <h4 className="text-orange-300 font-semibold mb-2">Bu Araç Ne İşe Yarar?</h4>
                  <ul className="text-sm text-gray-300 space-y-1.5">
                    <li className="flex items-start gap-2">
                      <span className="text-orange-400 mt-0.5">•</span>
                      <span>Etiketlenmiş Word dosyasındaki tüm etiketleri temizler</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-orange-400 mt-0.5">•</span>
                      <span>Dosya içeriği korunur, sadece etiketler kaldırılır</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-orange-400 mt-0.5">•</span>
                      <span>Temizlenen dosya tekrar etiketlenebilir hale gelir</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-orange-400 mt-0.5">•</span>
                      <span>Süreci yeniden başlatmak için kullanılabilir</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* File Upload Area */}
            <div className="mb-6">
              <div
                onDrop={handleCleanerDrop}
                onDragOver={(e) => e.preventDefault()}
                className="border-2 border-dashed border-gray-600 hover:border-orange-500 rounded-xl p-8 text-center transition-colors"
              >
                {!cleanerFile ? (
                  <div>
                    <svg className="w-12 h-12 mx-auto mb-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"></path>
                    </svg>
                    <p className="text-white font-medium mb-2">Etiketlenmiş dosyayı sürükle-bırak</p>
                    <p className="text-gray-400 text-sm mb-4">veya</p>
                    <label className="inline-flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg cursor-pointer transition-colors">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"></path>
                      </svg>
                      Dosya Seç
                      <input
                        type="file"
                        onChange={handleCleanerFileChange}
                        accept=".docx,.doc"
                        className="hidden"
                      />
                    </label>
                  </div>
                ) : (
                  <div className="flex items-center justify-between p-4 bg-gray-800 rounded-lg">
                    <div className="flex items-center gap-3">
                      <svg className="w-8 h-8 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                      </svg>
                      <div className="text-left">
                        <p className="text-white font-medium">{cleanerFile.name}</p>
                        <p className="text-gray-400 text-sm">{(cleanerFile.size / 1024 / 1024).toFixed(2)} MB</p>
                      </div>
                    </div>
                    {!isCleaningFile && (
                      <button
                        onClick={() => setCleanerFile(null)}
                        className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
                      >
                        <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                        </svg>
                      </button>
                    )}
                  </div>
                )}
              </div>

              {/* Progress Bar */}
              {isCleaningFile && (
                <div className="mt-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-gray-400">Dosya temizleniyor...</span>
                    <span className="text-sm text-orange-400 font-semibold">{cleanerProgress}%</span>
                  </div>
                  <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-orange-500 to-orange-600 transition-all duration-300"
                      style={{ width: `${cleanerProgress}%` }}
                    ></div>
                  </div>
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowTagCleanerModal(false);
                  setCleanerFile(null);
                  setCleanerProgress(0);
                }}
                disabled={isCleaningFile}
                className="flex-1 px-4 py-2.5 bg-gray-700 hover:bg-gray-600 text-gray-200 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                İptal
              </button>
              <button
                onClick={handleCleanFile}
                disabled={!cleanerFile || isCleaningFile}
                className="flex-1 px-4 py-2.5 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white rounded-lg font-medium transition-colors shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isCleaningFile ? 'Temizleniyor...' : 'Temizle ve İndir'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Company Code Modal */}
      {showEditCodeModal && companyToEditCode && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="rounded-2xl shadow-2xl max-w-md w-full p-6 border border-gray-200 dark:border-gray-700" style={{ backgroundColor: '#111827' }}>
            {/* Header */}
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-blue-500/20 dark:bg-blue-500/30 rounded-xl flex items-center justify-center">
                <svg className="w-6 h-6 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path>
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-bold text-white">Şirket Kodunu Düzenle</h3>
                <p className="text-sm text-gray-400">Yeni şirket kodunu girin</p>
              </div>
            </div>

            {/* Current Info Display */}
            <div className="mb-4 p-3 bg-gray-800/50 rounded-lg border border-gray-700">
              <p className="text-xs text-gray-400 mb-1">Şirket:</p>
              <p className="text-sm text-white font-medium">{companyToEditCode.name}</p>
              <p className="text-xs text-gray-400 mt-2 mb-1">Mevcut Kod:</p>
              <p className="text-sm text-white font-medium">{companyToEditCode.company_code}</p>
            </div>

            {/* Input */}
            <div className="mb-6">
              <label htmlFor="newCompanyCode" className="block text-sm font-medium text-gray-300 mb-2">
                Yeni Şirket Kodu
              </label>
              <input
                type="text"
                id="newCompanyCode"
                value={editedCompanyCode}
                onChange={(e) => setEditedCompanyCode(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleConfirmEditCode();
                  }
                }}
                placeholder="Yeni şirket kodunu girin"
                className="w-full px-4 py-2.5 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                autoFocus
              />
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowEditCodeModal(false);
                  setCompanyToEditCode(null);
                  setEditedCompanyCode('');
                }}
                className="flex-1 px-4 py-2.5 bg-gray-700 hover:bg-gray-600 text-gray-200 rounded-lg font-medium transition-colors"
              >
                İptal
              </button>
              <button
                onClick={handleConfirmEditCode}
                disabled={!editedCompanyCode.trim()}
                className="flex-1 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Devam Et
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Company Code Confirmation Modal */}
      {showEditCodeConfirmModal && companyToEditCode && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="rounded-2xl shadow-2xl max-w-md w-full p-6 border border-gray-200 dark:border-gray-700" style={{ backgroundColor: '#111827' }}>
            {/* Header */}
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-yellow-500/20 dark:bg-yellow-500/30 rounded-xl flex items-center justify-center">
                <svg className="w-6 h-6 text-yellow-600 dark:text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path>
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-bold text-white">Şirket Kodunu Değiştir</h3>
                <p className="text-sm text-gray-400">Onaylıyor musunuz?</p>
              </div>
            </div>

            {/* Content */}
            <div className="mb-6 p-4 bg-gray-900/50 rounded-xl border border-gray-700 space-y-3">
              <div>
                <p className="text-xs text-gray-400 mb-1">Şirket:</p>
                <p className="text-sm text-white font-medium">{companyToEditCode.name}</p>
              </div>
              <div>
                <p className="text-xs text-gray-400 mb-1">Eski Kod:</p>
                <p className="text-sm text-gray-300">{companyToEditCode.company_code}</p>
              </div>
              <div className="flex items-center justify-center">
                <svg className="w-5 h-5 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 14l-7 7m0 0l-7-7m7 7V3"></path>
                </svg>
              </div>
              <div>
                <p className="text-xs text-gray-400 mb-1">Yeni Kod:</p>
                <p className="text-sm text-white font-semibold">{editedCompanyCode}</p>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowEditCodeConfirmModal(false);
                  setShowEditCodeModal(true);
                }}
                disabled={isUpdatingCode}
                className="flex-1 px-4 py-2.5 bg-gray-700 hover:bg-gray-600 text-gray-200 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Geri
              </button>
              <button
                onClick={handleUpdateCompanyCode}
                disabled={isUpdatingCode}
                className="flex-1 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isUpdatingCode ? (
                  <>
                    <svg className="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
                    </svg>
                    <span>Güncelleniyor...</span>
                  </>
                ) : (
                  'Onayla ve Güncelle'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Document Name Modal */}
      {showEditNameModal && documentToEdit && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="rounded-2xl shadow-2xl max-w-md w-full p-6 border border-gray-200 dark:border-gray-700" style={{ backgroundColor: '#111827' }}>
            {/* Header */}
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-blue-500/20 dark:bg-blue-500/30 rounded-xl flex items-center justify-center">
                <svg className="w-6 h-6 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path>
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-bold text-white">Dosya Adını Düzenle</h3>
                <p className="text-sm text-gray-400">Yeni dosya adını girin</p>
              </div>
            </div>

            {/* Current Name Display */}
            <div className="mb-4 p-3 bg-gray-800/50 rounded-lg border border-gray-700">
              <p className="text-xs text-gray-400 mb-1">Mevcut Dosya Adı:</p>
              <p className="text-sm text-white font-medium break-words">{documentToEdit.file_name}</p>
            </div>

            {/* Input */}
            <div className="mb-6">
              <label htmlFor="newDocumentName" className="block text-sm font-medium text-gray-300 mb-2">
                Yeni Dosya Adı
              </label>
              <input
                type="text"
                id="newDocumentName"
                value={newDocumentName}
                onChange={(e) => setNewDocumentName(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleConfirmEditName();
                  }
                }}
                placeholder="Yeni dosya adını girin"
                className="w-full px-4 py-2.5 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                autoFocus
              />
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowEditNameModal(false);
                  setDocumentToEdit(null);
                  setNewDocumentName('');
                }}
                className="flex-1 px-4 py-2.5 bg-gray-700 hover:bg-gray-600 text-gray-200 rounded-lg font-medium transition-colors"
              >
                İptal
              </button>
              <button
                onClick={handleConfirmEditName}
                disabled={!newDocumentName.trim()}
                className="flex-1 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Devam Et
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Document Name Confirmation Modal */}
      {showEditNameConfirmModal && documentToEdit && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="rounded-2xl shadow-2xl max-w-md w-full p-6 border border-gray-200 dark:border-gray-700" style={{ backgroundColor: '#111827' }}>
            {/* Header */}
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-yellow-500/20 dark:bg-yellow-500/30 rounded-xl flex items-center justify-center">
                <svg className="w-6 h-6 text-yellow-600 dark:text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path>
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-bold text-white">Dosya Adını Değiştir</h3>
                <p className="text-sm text-gray-400">Onaylıyor musunuz?</p>
              </div>
            </div>

            {/* Content */}
            <div className="mb-6 p-4 bg-gray-900/50 rounded-xl border border-gray-700 space-y-3">
              <div>
                <p className="text-xs text-gray-400 mb-1">Eski Ad:</p>
                <p className="text-sm text-gray-300 break-words">{documentToEdit.file_name}</p>
              </div>
              <div className="flex items-center justify-center">
                <svg className="w-5 h-5 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 14l-7 7m0 0l-7-7m7 7V3"></path>
                </svg>
              </div>
              <div>
                <p className="text-xs text-gray-400 mb-1">Yeni Ad:</p>
                <p className="text-sm text-white font-semibold break-words">{newDocumentName}</p>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowEditNameConfirmModal(false);
                  setShowEditNameModal(true);
                }}
                disabled={isUpdatingName}
                className="flex-1 px-4 py-2.5 bg-gray-700 hover:bg-gray-600 text-gray-200 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Geri
              </button>
              <button
                onClick={handleUpdateDocumentName}
                disabled={isUpdatingName}
                className="flex-1 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isUpdatingName ? (
                  <>
                    <svg className="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
                    </svg>
                    <span>Güncelleniyor...</span>
                  </>
                ) : (
                  'Onayla ve Güncelle'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Report Voice Selection Modal */}
      {showReportVoiceModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="rounded-2xl shadow-2xl max-w-2xl w-full p-6 border border-gray-200 dark:border-gray-700" style={{ backgroundColor: '#111827' }}>
            {/* Header */}
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-blue-500/20 dark:bg-blue-500/30 rounded-xl flex items-center justify-center">
                <svg className="w-6 h-6 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z"></path>
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-bold text-white">Rapor Dili Seçimi</h3>
                <p className="text-sm text-gray-400">Raporunuz için kullanılacak dili seçin</p>
              </div>
            </div>

            {/* Options */}
            <div className="space-y-3 mb-6">
              {/* Option 1: Kurumsal Raporlama Dili */}
              <button
                onClick={() => setReportVoice('corporate')}
                className={`w-full text-left p-4 rounded-xl border-2 transition-all ${
                  reportVoice === 'corporate'
                    ? 'border-blue-500 bg-blue-500/10'
                    : 'border-gray-700 bg-gray-800/50 hover:border-gray-600'
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className={`mt-1 w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                    reportVoice === 'corporate'
                      ? 'border-blue-500 bg-blue-500'
                      : 'border-gray-600'
                  }`}>
                    {reportVoice === 'corporate' && (
                      <div className="w-2 h-2 bg-white rounded-full"></div>
                    )}
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-white mb-1">Kurumsal Raporlama Dili</h4>
                    <p className="text-sm text-gray-400 mb-2">Üçüncü tekil şahıs anlatımı. Özne şirkettir.</p>
                    <p className="text-xs text-gray-500 italic">Örnek: "Yapmaktadır, açıklamaktadır, sunmaktadır"</p>
                  </div>
                </div>
              </button>

              {/* Option 2: Biz'li Dil */}
              <button
                onClick={() => setReportVoice('we_lang')}
                className={`w-full text-left p-4 rounded-xl border-2 transition-all ${
                  reportVoice === 'we_lang'
                    ? 'border-blue-500 bg-blue-500/10'
                    : 'border-gray-700 bg-gray-800/50 hover:border-gray-600'
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className={`mt-1 w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                    reportVoice === 'we_lang'
                      ? 'border-blue-500 bg-blue-500'
                      : 'border-gray-600'
                  }`}>
                    {reportVoice === 'we_lang' && (
                      <div className="w-2 h-2 bg-white rounded-full"></div>
                    )}
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-white mb-1">Biz'li Dil (Birinci Çoğul Şahıs)</h4>
                    <p className="text-sm text-gray-400 mb-2">Birinci çoğul şahıs anlatımı.</p>
                    <p className="text-xs text-gray-500 italic">Örnek: "Yapıyoruz, açıklıyoruz, sunuyoruz"</p>
                  </div>
                </div>
              </button>

              {/* Option 3: Hibrit Dil */}
              <button
                onClick={() => setReportVoice('hybrid')}
                className={`w-full text-left p-4 rounded-xl border-2 transition-all ${
                  reportVoice === 'hybrid'
                    ? 'border-blue-500 bg-blue-500/10'
                    : 'border-gray-700 bg-gray-800/50 hover:border-gray-600'
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className={`mt-1 w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                    reportVoice === 'hybrid'
                      ? 'border-blue-500 bg-blue-500'
                      : 'border-gray-600'
                  }`}>
                    {reportVoice === 'hybrid' && (
                      <div className="w-2 h-2 bg-white rounded-full"></div>
                    )}
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-white mb-1">Hibrit Dil</h4>
                    <p className="text-sm text-gray-400 mb-2">Şirket ve ana faaliyetler için "Biz'li Dil", finansal ve kurumsal yönetim için "Kurumsal Raporlama Dili".</p>
                    <p className="text-xs text-gray-500 italic">İki dil stilinin birleşimi</p>
                  </div>
                </div>
              </button>
            </div>

            {/* Entity Name Section */}
            <div className="mt-6 pt-6 border-t border-gray-700">
              <div className="space-y-4">
                {/* Entity Name */}
                <div>
                  <label htmlFor="entityName" className="block text-base font-semibold text-white mb-1">
                    Kurum Adı
                  </label>
                  <p className="text-sm text-gray-400 mb-3">Raporda kullanılacak kurum adını girin</p>
                  <input
                    type="text"
                    id="entityName"
                    value={entityName}
                    onChange={(e) => { setEntityName(e.target.value); if (e.target.value.trim()) setEntityNameError(''); }}
                    placeholder="Örn: Finar Kurumsal A.Ş."
                    className={`w-full px-4 py-3 bg-gray-800 border rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${entityNameError ? 'border-red-500' : 'border-gray-600'}`}
                  />
                  {entityNameError && (
                    <p className="mt-1.5 text-sm text-red-400">{entityNameError}</p>
                  )}
                </div>

                {/* Entity Type */}
                <div>
                  <label htmlFor="entityType" className="block text-base font-semibold text-white mb-1">
                    Kurum Türü
                  </label>
                  <p className="text-sm text-gray-400 mb-3">Kurum türünü seçin</p>
                  <select
                    id="entityType"
                    value={entityType}
                    onChange={(e) => setEntityType(e.target.value as 'bank' | 'company' | 'holding' | 'foundation')}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all cursor-pointer"
                  >
                    <option value="company">Şirket</option>
                    <option value="bank">Banka</option>
                    <option value="holding">Holding</option>
                    <option value="foundation">Vakıf</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowReportVoiceModal(false)}
                className="flex-1 px-4 py-2.5 bg-gray-700 hover:bg-gray-600 text-gray-200 rounded-lg font-medium transition-colors"
              >
                İptal
              </button>
              <button
                onClick={performUpload}
                className="flex-1 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors shadow-md hover:shadow-lg"
              >
                Yükle
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FRUpdatePage
